import { useState, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { Modal } from '@/components/ui';
import { LeadPurchases } from './LeadPurchases';
import type { Lead } from '@/lib/types';

/** Produtos que cabem na célula; o resto vira "+N". */
const PRODUCTS_SHOWN = 1;
/** Distância entre a célula e o tooltip. */
const TIP_GAP = 6;

/**
 * Os produtos que o cliente comprou na Ephra, em selos. Passar o mouse mostra
 * a lista toda; clicar abre as compras num modal (sem abrir o detalhe da linha).
 */
export function ProductCell({ lead }: { lead: Lead }) {
  const [tip, setTip] = useState<{ left: number; top: number } | null>(null);
  const [open, setOpen] = useState(false);
  const { products } = lead;

  if (products.length === 0) return <span className="muted">—</span>;
  const rest = products.length - PRODUCTS_SHOWN;

  // O tooltip vai para o body: a tabela rola de lado e cortaria um absoluto.
  const showTip = (target: HTMLElement) => {
    const box = target.getBoundingClientRect();
    setTip({ left: box.left, top: box.bottom + TIP_GAP });
  };
  const openModal = (event: MouseEvent) => {
    event.stopPropagation();
    setTip(null);
    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        className="product-chips"
        aria-label={`Ver compras: ${products.join(', ')}`}
        onMouseEnter={(event) => showTip(event.currentTarget)}
        onMouseLeave={() => setTip(null)}
        onFocus={(event) => showTip(event.currentTarget)}
        onBlur={() => setTip(null)}
        onClick={openModal}
      >
        {products.slice(0, PRODUCTS_SHOWN).map((product) => <span key={product} className="chip">{product}</span>)}
        {rest > 0 ? <span className="chip">+{rest}</span> : null}
      </button>

      {tip ? createPortal(
        <div className="product-tip" role="tooltip" style={{ left: tip.left, top: tip.top }}>
          <div className="product-tip-head">
            {products.length === 1 ? '1 produto' : `${products.length} produtos`}
          </div>
          <ul>
            {products.map((product) => <li key={product}>{product}</li>)}
          </ul>
          <div className="product-tip-foot">Clique para ver as compras</div>
        </div>,
        document.body,
      ) : null}

      {open ? (
        // O modal fica dentro da linha; os cliques nele não podem abrir/fechar o detalhe.
        <div onClick={(event) => event.stopPropagation()}>
          <Modal title={`Compras de ${lead.name}`} onClose={() => setOpen(false)} width={680}>
            <LeadPurchases purchases={lead.purchases} />
          </Modal>
        </div>
      ) : null}
    </>
  );
}
